/**
 * Reading the commentary out loud.
 *
 * The demonstration is a thing to watch, and watching a board while reading a
 * caption underneath it means doing neither properly. The browser's own
 * speech synthesiser solves that with no download and no audio files, which
 * matches how the rest of the sound in this game works.
 *
 * Everything here is defensive on purpose. `speechSynthesis` is missing
 * entirely in some browsers, present but voiceless in others until a voice
 * list arrives asynchronously, and on iOS it will not utter a word until it
 * has been spoken to from inside a real user gesture. None of that is worth
 * an error: the captions say the same words, so silence is a degradation
 * rather than a failure.
 */

const supported = (): boolean =>
  typeof window !== 'undefined' && 'speechSynthesis' in window
  && typeof SpeechSynthesisUtterance === 'function';

/** An English voice, preferring a local one — remote voices lag and cost data. */
export function pickVoice(): SpeechSynthesisVoice | null {
  try {
    const all = speechSynthesis.getVoices();
    const english = all.filter((v) => /^en\b/i.test(v.lang));
    return english.find((v) => v.localService && /Daniel|Serena|Kate|Martha|Google UK/i.test(v.name))
      ?? english.find((v) => v.localService)
      ?? english[0]
      ?? null;
  } catch {
    return null;
  }
}

export class Narrator {
  #enabled = false;
  #voice: SpeechSynthesisVoice | null = null;

  get available(): boolean { return supported(); }
  get enabled(): boolean { return this.#enabled && supported(); }

  /**
   * Turn narration on or off.
   *
   * Must be called from a user gesture the first time, or iOS ignores every
   * later utterance. The voice list also arrives late on some browsers, so it
   * is looked up again on the `voiceschanged` event rather than once.
   */
  enable(on: boolean): void {
    if (!supported()) return;
    this.#enabled = on;
    if (!on) { this.stop(); return; }
    this.#voice = pickVoice();
    if (!this.#voice) {
      try {
        speechSynthesis.addEventListener('voiceschanged',
          () => { this.#voice = pickVoice(); }, { once: true });
      } catch { /* not supported; the default voice will do */ }
    }
  }

  /**
   * Say something, cancelling whatever was being said, and report when it is
   * done.
   *
   * `onDone` is what lets the demonstration wait for the voice instead of
   * talking over itself. It fires on the utterance's end or error — and, as a
   * backstop, after a generous estimate of how long the words take, because
   * some browsers occasionally never report the end of an utterance at all,
   * and a demonstration must not stall for ever on that.
   */
  say(text: string, onDone?: () => void): void {
    this.#finish = null;
    if (!this.enabled) { onDone?.(); return; }
    let called = false;
    const done = (): void => {
      if (called) return;
      called = true;
      window.clearTimeout(backstop);
      // Only the utterance still wanted may move things on. Cancelling one to
      // start the next makes the old one report an error — which, acted on,
      // would skip the demonstration ahead a beat.
      if (this.#finish !== done) return;
      this.#finish = null;
      onDone?.();
    };
    this.#finish = done;
    // About 13 characters a second at this rate, plus a margin — and when the
    // estimate runs out, only give up if the voice has actually stopped. A
    // slow voice must never be cut off by a timer that guessed short; the cap
    // is for a browser that never reports the end at all.
    const started = Date.now();
    let backstop = 0;
    const check = (): void => {
      let talking = false;
      try { talking = speechSynthesis.speaking; } catch { /* treat as stopped */ }
      if (talking && Date.now() - started < 30_000) {
        backstop = window.setTimeout(check, 500);
      } else {
        done();
      }
    };
    backstop = window.setTimeout(check, (text.length / 13) * 1000 + 2500);
    try {
      speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      if (this.#voice) u.voice = this.#voice;
      u.lang = this.#voice?.lang ?? 'en-GB';
      // A shade slower than default: this is an explanation, not a reading.
      u.rate = 0.95;
      u.pitch = 1;
      u.onend = done;
      u.onerror = done;
      speechSynthesis.speak(u);
    } catch {
      done();
    }
  }

  #finish: (() => void) | null = null;

  /** Stop talking. A pending "done" is dropped, not fired: nothing waits on it now. */
  stop(): void {
    this.#finish = null;
    try { speechSynthesis.cancel(); } catch { /* ignore */ }
  }
}

export const narrator = new Narrator();
